/** Campaign foundation + audience domain types (Orders 30A / 30B.1). WhatsApp is the v1 channel. */
import { z } from "zod";
import { messageCreditsSchema } from "./message-balance-contracts.js";
import { apiBigIntIdSchema, apiIntIdSchema } from "./ids.js";
export const CAMPAIGN_STATUSES = [
    "DRAFT",
    "SCHEDULED",
    "RUNNING",
    "COMPLETED",
    "CANCELLED",
    "FAILED",
];
/**
 * Pre-message recipient orchestration only.
 * Provider SENT/DELIVERED/READ/FAILED stay on Message once linked.
 */
export const CAMPAIGN_RECIPIENT_EXECUTION_STATUSES = [
    "PENDING_VALIDATION",
    "EXCLUDED",
    "READY",
    "MATERIALIZING",
    "MESSAGE_CREATED",
    "CANCELLED_BEFORE_SEND",
];
export const CAMPAIGN_VARIABLE_SOURCES = [
    "CONTACT_FIELD",
    "UPLOAD_COLUMN",
    "CONSTANT",
];
/** Contact scalar fields available for Campaign variable mapping today. */
export const CAMPAIGN_CONTACT_FIELD_KEYS = [
    "firstName",
    "lastName",
    "displayName",
    "email",
    "normalizedPhoneNumber",
];
export const campaignVariableMappingEntrySchema = z.discriminatedUnion("source", [
    z.object({
        source: z.literal("CONTACT_FIELD"),
        field: z.enum(CAMPAIGN_CONTACT_FIELD_KEYS),
    }),
    z.object({
        source: z.literal("UPLOAD_COLUMN"),
        column: z.string().min(1).max(64),
    }),
    z.object({
        source: z.literal("CONSTANT"),
        value: z.string().min(1).max(1024),
    }),
]);
export const campaignVariableMappingSchema = z.object({
    version: z.literal(1),
    variables: z.record(z.string().min(1).max(64), campaignVariableMappingEntrySchema),
});
export function emptyCampaignVariableMapping() {
    return { version: 1, variables: {} };
}
export const campaignAudienceDefinitionSchema = z.object({
    version: z.literal(1),
    contactIds: z.array(apiIntIdSchema).default([]),
    contactGroupIds: z.array(apiIntIdSchema).default([]),
    upload: z
        .object({
        uploadId: z.string().min(1),
    })
        .nullable()
        .optional(),
});
export const updateCampaignAudienceDefinitionRequestSchema = z.object({
    contactIds: z.array(apiIntIdSchema).max(10_000).optional(),
    contactGroupIds: z.array(apiIntIdSchema).max(500).optional(),
    /** Omitted = this source was not modified. `{ uploadId }` replaces the CSV source. */
    upload: z
        .object({
        uploadId: z.string().min(1),
    })
        .optional(),
});
export const campaignTemplateSnapshotSchema = z.object({
    version: z.literal(1),
    whatsappTemplateId: apiIntIdSchema.nullable().optional(),
    providerTemplateId: z.string().min(1).nullable().optional(),
    name: z.string().min(1).nullable().optional(),
    language: z.string().min(1).nullable().optional(),
    category: z.string().min(1).nullable().optional(),
    parameterFormat: z.string().min(1).nullable().optional(),
    components: z.unknown().optional(),
});
export function emptyCampaignTemplateSnapshot() {
    return { version: 1 };
}
/**
 * True when sender + approved template snapshot + mapping cover every required slot.
 * `requiredParameterKeys` come from discoverTemplateSendDefinition (or equivalent).
 */
export function isCampaignMessageConfigured(input) {
    if (input.platformEndpointId == null || input.whatsappTemplateId == null) {
        return false;
    }
    if (input.templateSnapshot.version !== 1) {
        return false;
    }
    if (input.templateSnapshot.whatsappTemplateId !== input.whatsappTemplateId) {
        return false;
    }
    if (input.variableMapping.version !== 1) {
        return false;
    }
    const variables = input.variableMapping.variables;
    const keys = input.requiredParameterKeys;
    if (Object.keys(variables).length !== keys.length) {
        return false;
    }
    return keys.every((key) => variables[key] != null);
}
export const CAMPAIGN_AUDIENCE_ISSUE_REASONS = [
    "MISSING_PHONE",
    "INVALID_PHONE",
    "MISSING_COUNTRY",
    "CONTACT_NOT_FOUND",
    "CONTACT_DELETED",
    "GROUP_NOT_FOUND",
    "UPLOAD_ROW_INVALID",
    "DUPLICATE_RECIPIENT",
    "DUPLICATE_RECIPIENT_CONFLICT",
    "PERSONALIZATION_PAYLOAD_TOO_LARGE",
    "UPLOAD_NOT_READY",
    "UPLOAD_EXPIRED",
];
export const campaignAudienceSnapshotSummarySchema = z.object({
    version: z.literal(1),
    selectedSourceEntries: z.number().int().nonnegative(),
    canonicalRecipients: z.number().int().nonnegative(),
    readyRecipients: z.number().int().nonnegative(),
    excludedBeforeRecipient: z.number().int().nonnegative(),
    duplicateSourceEntries: z.number().int().nonnegative(),
    byReason: z.record(z.string(), z.number().int().nonnegative()),
});
/** StructuredData provenance for Campaign outbound (30E) and Send Test (30D). */
export const CAMPAIGN_MESSAGE_PROVENANCE_KIND = "campaign_outbound";
export const CAMPAIGN_TEST_MESSAGE_PROVENANCE_KIND = "campaign_send_test";
export const createCampaignRequestSchema = z.object({
    name: z.string().trim().min(1).max(200),
});
export const campaignAudienceIssueSampleSchema = z.object({
    reason: z.enum(CAMPAIGN_AUDIENCE_ISSUE_REASONS),
    contactId: apiIntIdSchema.nullable().optional(),
    groupId: apiIntIdSchema.nullable().optional(),
    rowNumber: z.number().int().positive().optional(),
});
export const campaignAudiencePreviewRecipientSampleSchema = z.object({
    normalizedPhoneE164: z.string().min(1),
    contactId: apiIntIdSchema.nullable(),
    displayName: z.string().nullable(),
    sourceTypes: z.array(z.enum(["CONTACT", "GROUP", "UPLOAD"])),
});
/**
 * Campaign execution pace + schedule display context (Order 30E / Phase F / Phase G).
 * Product ceilings are server-enforced. Auto has no fixed MPS; Custom product max is 80
 * (Phase G approved). Separate from WHATSAPP_SENDER_MAX_IN_FLIGHT (per-sender concurrency).
 */
export const CAMPAIGN_EXECUTION_LIMITS = {
    /** Bounded recipient claim/materialize batch per Worker tick. */
    recipientBatchSize: 25,
    /** Minimum Custom pace (messages/second). */
    minMessagesPerSecond: 1,
    /** Hard product cap for Custom pace (messages/second). API write path enforces this. */
    maxMessagesPerSecond: 80,
    /**
     * Default Custom form value when the operator switches Auto → Custom.
     * Not an Auto rate; Auto has no MPS.
     */
    defaultCustomMessagesPerSecond: 10,
    /** Minimum lead time before a schedule instant is accepted. */
    scheduleMinLeadSeconds: 60,
    /** Stale MATERIALIZING reclaim window. */
    materializingStaleMs: 5 * 60 * 1000,
};
/**
 * Stored `executionSettings.customMessagesPerSecond` ceiling for Worker reads and
 * Phase G benchmark harnesses that write JSON directly. Product API requests still
 * reject values above {@link CAMPAIGN_EXECUTION_LIMITS.maxMessagesPerSecond}.
 */
export const CAMPAIGN_CUSTOM_MPS_STORED_CEILING = 1_000;
export const CAMPAIGN_PACE_MODES = ["AUTO", "CUSTOM"];
export const CAMPAIGN_EXCLUSION_REASON_CODES = [
    "MISSING_PERSONALIZATION",
    "CONTACT_DELETED",
    "CONTACT_BLOCKED",
    "MATERIALIZATION_FAILED",
];
export const campaignExecutionSettingsSchema = z.object({
    version: z.literal(1),
    paceMode: z.enum(CAMPAIGN_PACE_MODES),
    /**
     * Present only when paceMode is CUSTOM. Stored form may exceed the product API
     * max (Phase G benchmarks); API write validation still uses maxMessagesPerSecond.
     */
    customMessagesPerSecond: z
        .number()
        .int()
        .min(CAMPAIGN_EXECUTION_LIMITS.minMessagesPerSecond)
        .max(CAMPAIGN_CUSTOM_MPS_STORED_CEILING)
        .nullable(),
    /** IANA timezone used when the operator scheduled (Organization.timezone). */
    scheduleTimezone: z.string().min(1).max(64).nullable(),
});
export function emptyCampaignExecutionSettings() {
    return {
        version: 1,
        paceMode: "AUTO",
        customMessagesPerSecond: null,
        scheduleTimezone: null,
    };
}
/**
 * Custom MPS for paced materialization, or null when Auto (backpressure-only).
 * Does not clamp: invalid Custom settings must fail validation before this runs.
 */
export function resolveCampaignCustomMessagesPerSecond(settings) {
    if (settings.paceMode !== "CUSTOM" || settings.customMessagesPerSecond == null) {
        return null;
    }
    return settings.customMessagesPerSecond;
}
export const campaignDtoSchema = z.object({
    id: apiIntIdSchema,
    organizationId: apiIntIdSchema,
    name: z.string(),
    status: z.enum(CAMPAIGN_STATUSES),
    platformEndpointId: apiIntIdSchema.nullable(),
    whatsappTemplateId: apiIntIdSchema.nullable(),
    templateSnapshot: z.preprocess((value) => {
        if (value == null || (typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0)) {
            return emptyCampaignTemplateSnapshot();
        }
        return value;
    }, campaignTemplateSnapshotSchema),
    variableMapping: z.preprocess((value) => {
        if (value == null || (typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0)) {
            return emptyCampaignVariableMapping();
        }
        return value;
    }, campaignVariableMappingSchema),
    messageConfigured: z.boolean(),
    audienceDefinition: campaignAudienceDefinitionSchema,
    audienceDefinitionRevision: z.number().int().positive(),
    audienceSnapshotRevision: z.number().int().positive().nullable(),
    audienceSnapshotAt: z.string().datetime({ offset: true }).nullable(),
    audienceSnapshotSummary: campaignAudienceSnapshotSummarySchema.nullable(),
    snapshotStale: z.boolean(),
    scheduledAt: z.string().datetime({ offset: true }).nullable(),
    startedAt: z.string().datetime({ offset: true }).nullable(),
    completedAt: z.string().datetime({ offset: true }).nullable(),
    failureCode: z.string().nullable(),
    failureSummary: z.string().nullable(),
    executionSettings: z.preprocess((value) => {
        if (value == null || (typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0)) {
            return emptyCampaignExecutionSettings();
        }
        return value;
    }, campaignExecutionSettingsSchema),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const updateCampaignMessageRequestSchema = z.object({
    platformEndpointId: apiIntIdSchema,
    whatsappTemplateId: apiIntIdSchema,
    variableMapping: campaignVariableMappingSchema,
});
export const campaignMessageCoverageEntrySchema = z.object({
    parameterKey: z.string().min(1),
    componentType: z.string().min(1).optional(),
    mappedSource: z.string().min(1),
    presentCount: z.number().int().nonnegative(),
    totalReadyCount: z.number().int().nonnegative(),
    missingCount: z.number().int().nonnegative(),
});
export const campaignMessageCoverageResponseSchema = z.object({
    coverage: z.array(campaignMessageCoverageEntrySchema),
    messageConfigured: z.boolean(),
});
export const updateCampaignMessageResponseSchema = z.object({
    campaign: campaignDtoSchema,
    coverage: z.array(campaignMessageCoverageEntrySchema).optional(),
    messageConfigured: z.boolean(),
});
export const createCampaignResponseSchema = z.object({
    campaign: campaignDtoSchema,
});
export const getCampaignResponseSchema = createCampaignResponseSchema;
export const DEFAULT_CAMPAIGN_LIST_PAGE_SIZE = 25;
export const CAMPAIGN_LIST_PAGE_SIZES = [25, 50, 100];
/** Sidebar navigation statuses. FAILED and CANCELLED stay valid Campaign statuses but are not nav categories. */
export const CAMPAIGN_LIST_NAV_STATUSES = [
    "DRAFT",
    "SCHEDULED",
    "RUNNING",
    "COMPLETED",
];
export function isCampaignListNavStatus(value) {
    return (value === "DRAFT" ||
        value === "SCHEDULED" ||
        value === "RUNNING" ||
        value === "COMPLETED");
}
export const campaignListStatusCountsSchema = z.object({
    all: z.number().int().nonnegative(),
    DRAFT: z.number().int().nonnegative(),
    SCHEDULED: z.number().int().nonnegative(),
    RUNNING: z.number().int().nonnegative(),
    COMPLETED: z.number().int().nonnegative(),
});
export function emptyCampaignListStatusCounts() {
    return {
        all: 0,
        DRAFT: 0,
        SCHEDULED: 0,
        RUNNING: 0,
        COMPLETED: 0,
    };
}
/** Whitelisted Campaign list sort fields. Recipients are omitted — count is JSON-derived. */
export const CAMPAIGN_LIST_SORT_FIELDS = ["name", "status", "updatedAt", "scheduledAt"];
export const CAMPAIGN_LIST_SORT_DIRECTIONS = ["asc", "desc"];
export const MAX_CAMPAIGN_LIST_SORT_RULES = 4;
export const campaignListSortRuleSchema = z.object({
    field: z.enum(CAMPAIGN_LIST_SORT_FIELDS),
    direction: z.enum(CAMPAIGN_LIST_SORT_DIRECTIONS),
});
export function isCampaignListSortField(value) {
    return CAMPAIGN_LIST_SORT_FIELDS.includes(value);
}
export function serializeCampaignListSortParam(rules) {
    if (rules.length === 0)
        return undefined;
    return rules.map((rule) => `${rule.field}:${rule.direction}`).join(",");
}
function parseCampaignListSortToken(token) {
    const sep = token.indexOf(":");
    if (sep <= 0 || sep === token.length - 1) {
        return { field: token, direction: "" };
    }
    return { field: token.slice(0, sep), direction: token.slice(sep + 1) };
}
export function coerceCampaignListSortInput(value) {
    if (value == null || value === "")
        return undefined;
    if (Array.isArray(value)) {
        if (value.length === 0)
            return undefined;
        if (value.every((item) => item != null && typeof item === "object" && "field" in item && "direction" in item)) {
            return value;
        }
        if (value.every((item) => typeof item === "string")) {
            return coerceCampaignListSortInput(value.join(","));
        }
        return value;
    }
    if (typeof value !== "string")
        return value;
    const tokens = value
        .split(",")
        .map((token) => token.trim())
        .filter((token) => token.length > 0);
    if (tokens.length === 0)
        return undefined;
    return tokens.map(parseCampaignListSortToken);
}
export function uniqueCampaignListSortRules(rules) {
    const seen = new Set();
    const unique = [];
    for (const rule of rules) {
        if (seen.has(rule.field))
            continue;
        seen.add(rule.field);
        unique.push(rule);
        if (unique.length >= MAX_CAMPAIGN_LIST_SORT_RULES)
            break;
    }
    return unique;
}
export const campaignListQuerySchema = z.object({
    page: z.coerce.number().int().positive().optional(),
    pageSize: z.coerce.number().int().positive().max(100).optional(),
    status: z.preprocess((value) => (value === "" || value == null ? undefined : value), z.enum(CAMPAIGN_STATUSES).optional()),
    search: z.preprocess((value) => {
        if (value == null || value === "")
            return undefined;
        if (typeof value !== "string")
            return value;
        const trimmed = value.trim();
        return trimmed.length > 0 ? trimmed : undefined;
    }, z.string().max(200).optional()),
    sort: z.preprocess(coerceCampaignListSortInput, z.array(campaignListSortRuleSchema).min(1).max(MAX_CAMPAIGN_LIST_SORT_RULES).optional()),
});
/**
 * Compact Campaign row for the org list. Does not include audience definition
 * or the full snapshot payload.
 */
export const campaignListItemSchema = z.object({
    id: apiIntIdSchema,
    name: z.string(),
    status: z.enum(CAMPAIGN_STATUSES),
    snapshotStale: z.boolean(),
    readyRecipientCount: z.number().int().nonnegative().nullable(),
    scheduledAt: z.string().datetime({ offset: true }).nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const campaignListResponseSchema = z.object({
    items: z.array(campaignListItemSchema),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    totalCount: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
    statusCounts: campaignListStatusCountsSchema,
});
export const campaignBulkDeleteRequestSchema = z.object({
    campaignIds: z.array(apiIntIdSchema).min(1).max(100),
});
export const campaignBulkDeleteResponseSchema = z.object({
    deleted: z.number().int().nonnegative(),
});
export const updateCampaignAudienceDefinitionResponseSchema = z.object({
    campaign: campaignDtoSchema,
});
export const campaignAudiencePreviewResponseSchema = z.object({
    definitionRevision: z.number().int().positive(),
    snapshotStale: z.boolean(),
    summary: campaignAudienceSnapshotSummarySchema,
    recipientSamples: z.array(campaignAudiencePreviewRecipientSampleSchema),
    issueSamples: z.array(campaignAudienceIssueSampleSchema),
});
export const campaignAudienceCommitResponseSchema = z.object({
    campaign: campaignDtoSchema,
    summary: campaignAudienceSnapshotSummarySchema,
});
export const campaignAudienceSummaryResponseSchema = z.object({
    definitionRevision: z.number().int().positive(),
    snapshotRevision: z.number().int().positive().nullable(),
    snapshotAt: z.string().datetime({ offset: true }).nullable(),
    snapshotStale: z.boolean(),
    summary: campaignAudienceSnapshotSummarySchema.nullable(),
});
export const campaignRecipientListItemSchema = z.object({
    id: apiBigIntIdSchema,
    normalizedPhoneE164: z.string(),
    contactId: apiIntIdSchema.nullable(),
    executionStatus: z.enum(CAMPAIGN_RECIPIENT_EXECUTION_STATUSES),
    exclusionReasonCode: z.string().nullable(),
    sourceProvenance: z.object({
        version: z.literal(1),
        sources: z.array(z.discriminatedUnion("type", [
            z.object({ type: z.literal("CONTACT"), contactId: apiIntIdSchema }),
            z.object({
                type: z.literal("GROUP"),
                groupId: apiIntIdSchema,
                groupName: z.string(),
            }),
            z.object({
                type: z.literal("UPLOAD"),
                uploadId: z.string().min(1),
                rowNumber: z.number().int().positive(),
            }),
        ])),
    }),
    personalizationSnapshot: z.object({
        version: z.literal(1),
        contactFields: z.object({
            firstName: z.string().nullable(),
            lastName: z.string().nullable(),
            displayName: z.string().nullable(),
            email: z.string().nullable(),
            normalizedPhoneNumber: z.string().nullable(),
        }),
        uploadFields: z.record(z.string(), z.string()),
    }),
    createdAt: z.string().datetime({ offset: true }),
});
export const CAMPAIGN_RECIPIENT_PAGE_SIZES = [25, 50, 100];
export const DEFAULT_CAMPAIGN_RECIPIENT_LIST_LIMIT = 25;
export function isCampaignRecipientPageSize(value) {
    return CAMPAIGN_RECIPIENT_PAGE_SIZES.includes(value);
}
export const campaignRecipientListQuerySchema = z.object({
    cursor: apiBigIntIdSchema.optional(),
    limit: z.coerce
        .number()
        .int()
        .optional()
        .refine((value) => value == null || isCampaignRecipientPageSize(value), {
        message: "limit must be 25, 50, or 100",
    }),
});
export const campaignRecipientListResponseSchema = z.object({
    items: z.array(campaignRecipientListItemSchema),
    nextCursor: apiBigIntIdSchema.nullable(),
});
/** Empty audience definition for new DRAFT campaigns. */
export function emptyCampaignAudienceDefinition() {
    return {
        version: 1,
        contactIds: [],
        contactGroupIds: [],
        upload: null,
    };
}
export function isCampaignAudienceSnapshotStale(input) {
    return (input.audienceSnapshotRevision == null ||
        input.audienceSnapshotRevision !== input.audienceDefinitionRevision);
}
/**
 * Future Schedule/Launch invariant (30E — not implemented in 30B.1):
 * a Campaign is audience-launchable only when the snapshot is fully committed
 * and matches the current definition revision.
 */
export function isCampaignAudienceLaunchable(input) {
    return !isCampaignAudienceSnapshotStale(input);
}
/**
 * Locked Campaign CSV ingest limits (Order 30B.2).
 * Engineering ingest safety ceiling — NOT Meta messaging capacity / tiers.
 */
export const CAMPAIGN_CSV_INGEST_LIMITS = {
    maxActiveUploadsPerCampaign: 1,
    abandonedTtlHours: 48,
    maxSourceRows: 100_000,
    /** Temporary memory-safety ceiling for in-memory combined materialization — not Meta capacity. */
    maxFileBytes: 5 * 1024 * 1024,
    maxColumns: 32,
    maxHeaderLength: 64,
    maxValueLength: 512,
    maxPersonalizationSnapshotBytes: 4 * 1024,
};
export const CAMPAIGN_REVIEW_BLOCKER_CODES = [
    "NOT_DRAFT",
    "AUDIENCE_STALE",
    "AUDIENCE_EMPTY",
    "MESSAGE_NOT_CONFIGURED",
    "SENDER_UNAVAILABLE",
    "TEMPLATE_UNAVAILABLE",
    "MAPPING_INCOMPLETE",
    "TEMPLATE_UNSUPPORTED",
    "TEMPLATE_HEADER_IMAGE_MISSING",
    "NO_EXECUTABLE_RECIPIENTS",
    "INSUFFICIENT_MESSAGE_CREDITS",
];
export const campaignPersonalizationSnapshotSchema = z.object({
    version: z.literal(1),
    contactFields: z.object({
        firstName: z.string().nullable(),
        lastName: z.string().nullable(),
        displayName: z.string().nullable(),
        email: z.string().nullable(),
        normalizedPhoneNumber: z.string().nullable(),
    }),
    uploadFields: z.record(z.string(), z.string()),
});
/**
 * Review & Send unlocks while DRAFT when audience is current with recipients
 * and 30C message configuration is complete. Missing personalization values
 * are warnings, not a step lock.
 */
export function isCampaignReviewStepReady(input) {
    return (input.status === "DRAFT" &&
        !input.snapshotStale &&
        input.readyRecipientCount > 0 &&
        input.messageConfigured);
}
export const campaignSendTestRequestSchema = z.object({
    sampleRecipientId: apiBigIntIdSchema,
    destinationPhone: z.string().trim().min(1).max(32),
    defaultCountryIso: z
        .string()
        .trim()
        .length(2)
        .regex(/^[A-Za-z]{2}$/)
        .transform((value) => value.toUpperCase())
        .optional(),
    clientIdempotencyKey: z.uuid(),
});
export const campaignSendTestResponseSchema = z.object({
    success: z.literal(true),
    destination: z.string().min(1),
    messageId: apiBigIntIdSchema,
    campaignStatus: z.literal("DRAFT"),
});
export const campaignReviewBlockerSchema = z.object({
    code: z.enum(CAMPAIGN_REVIEW_BLOCKER_CODES),
});
export const campaignReviewResponseSchema = z.object({
    campaign: campaignDtoSchema,
    coverage: z.array(campaignMessageCoverageEntrySchema),
    reviewReady: z.boolean(),
    sendTestReady: z.boolean(),
    launchReady: z.boolean(),
    blockers: z.array(campaignReviewBlockerSchema),
    readyRecipientCount: z.number().int().nonnegative(),
    executableRecipientCount: z.number().int().nonnegative(),
    excludedRecipientCount: z.number().int().nonnegative(),
    organizationTimezone: z.string().min(1),
    requiredCredits: z.number().int().nonnegative().optional(),
    isUnlimited: z.boolean(),
    availableCredits: messageCreditsSchema.optional(),
    reservedCredits: messageCreditsSchema.optional(),
});
export const campaignPaceRequestSchema = z.discriminatedUnion("paceMode", [
    z.object({
        paceMode: z.literal("AUTO"),
    }),
    z.object({
        paceMode: z.literal("CUSTOM"),
        customMessagesPerSecond: z
            .number()
            .int()
            .min(CAMPAIGN_EXECUTION_LIMITS.minMessagesPerSecond)
            .max(CAMPAIGN_EXECUTION_LIMITS.maxMessagesPerSecond),
    }),
]);
export const campaignSendNowRequestSchema = z.object({
    launchIdempotencyKey: z.uuid(),
    pace: campaignPaceRequestSchema.optional(),
});
export const campaignScheduleRequestSchema = z.object({
    launchIdempotencyKey: z.uuid(),
    /** Local calendar date in organization timezone: YYYY-MM-DD */
    localDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** Local time in organization timezone: HH:mm */
    localTime: z.string().regex(/^\d{2}:\d{2}$/),
    /** Must match Organization.timezone (server rejects mismatch). */
    timezone: z.string().min(1).max(64),
    pace: campaignPaceRequestSchema.optional(),
});
export const campaignLaunchResponseSchema = z.object({
    campaignId: apiIntIdSchema,
    status: z.enum(CAMPAIGN_STATUSES),
    scheduledAt: z.string().datetime({ offset: true }).nullable(),
    readyRecipientCount: z.number().int().nonnegative(),
    excludedRecipientCount: z.number().int().nonnegative(),
    organizationTimezone: z.string().min(1),
});
export const campaignCancelScheduleResponseSchema = z.object({
    campaignId: apiIntIdSchema,
    status: z.literal("DRAFT"),
});
/** Whether Campaign definition (audience/message) may still be edited. */
export function isCampaignDefinitionEditable(status) {
    return status === "DRAFT";
}
/**
 * Campaign reporting result buckets (Order 30F). Derived read-model only —
 * not a durable CampaignRecipient status machine.
 *
 * Mutually exclusive precedence (highest first once a Message exists):
 * EXCLUDED → READ → DELIVERED → SENT → FAILED → PROCESSING
 *
 * EXCLUDED covers execution EXCLUDED and CANCELLED_BEFORE_SEND.
 * PROCESSING covers pre-message orchestration and linked Message PENDING/SENDING
 * (MessageSendJob not yet provider-accepted). MESSAGE_CREATED alone is never SENT.
 */
export const CAMPAIGN_RESULT_STATUSES = [
    "EXCLUDED",
    "PROCESSING",
    "SENT",
    "DELIVERED",
    "READ",
    "FAILED",
];
export const CAMPAIGN_RESULT_PAGE_SIZES = [25, 50, 100];
export const DEFAULT_CAMPAIGN_RESULT_PAGE_SIZE = 25;
export function isCampaignResultPageSize(value) {
    return CAMPAIGN_RESULT_PAGE_SIZES.includes(value);
}
/**
 * Derive the mutually exclusive Campaign reporting status from durable rows.
 * `messageStatus` is the projected Message.status when `messageId` is linked.
 */
export function deriveCampaignResultStatus(input) {
    switch (input.executionStatus) {
        case "EXCLUDED":
        case "CANCELLED_BEFORE_SEND":
            return "EXCLUDED";
        case "PENDING_VALIDATION":
        case "READY":
        case "MATERIALIZING":
        case "MESSAGE_CREATED":
            break;
        default: {
            const _exhaustive = input.executionStatus;
            return _exhaustive;
        }
    }
    if (input.messageStatus == null) {
        return "PROCESSING";
    }
    switch (input.messageStatus) {
        case "READ":
            return "READ";
        case "DELIVERED":
            return "DELIVERED";
        case "SENT":
            return "SENT";
        case "FAILED":
            return "FAILED";
        case "PENDING":
        case "SENDING":
        case "RECEIVED":
            return "PROCESSING";
        default: {
            const _exhaustive = input.messageStatus;
            return _exhaustive;
        }
    }
}
export const campaignResultsSummarySchema = z.object({
    campaignId: apiIntIdSchema,
    campaignStatus: z.enum(CAMPAIGN_STATUSES),
    total: z.number().int().nonnegative(),
    excluded: z.number().int().nonnegative(),
    processing: z.number().int().nonnegative(),
    sent: z.number().int().nonnegative(),
    delivered: z.number().int().nonnegative(),
    read: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    /**
     * True while orchestration is RUNNING or any linked Message is still
     * PENDING/SENDING (provider updates may still arrive after COMPLETED).
     */
    providerUpdatesPending: z.boolean(),
});
export const campaignResultRecipientRowSchema = z.object({
    recipientId: apiBigIntIdSchema,
    name: z.string().nullable(),
    phone: z.string(),
    email: z.string().nullable(),
    sourceTypes: z.array(z.enum(["CONTACT", "GROUP", "UPLOAD"])),
    campaignExecutionStatus: z.enum(CAMPAIGN_RECIPIENT_EXECUTION_STATUSES),
    resultStatus: z.enum(CAMPAIGN_RESULT_STATUSES),
    /** Projected Message.status when linked; null when no Message yet. */
    providerStatus: z
        .enum(["PENDING", "SENDING", "SENT", "DELIVERED", "READ", "FAILED"])
        .nullable(),
    exclusionReasonCode: z.string().nullable(),
    /** Sanitized outbound / webhook failure code when resultStatus is FAILED. */
    failureCode: z.string().nullable(),
    messageId: apiBigIntIdSchema.nullable(),
    materializedAt: z.string().datetime({ offset: true }).nullable(),
    messageCreatedAt: z.string().datetime({ offset: true }).nullable(),
    platformSentAt: z.string().datetime({ offset: true }).nullable(),
});
export const campaignResultsRecipientsQuerySchema = z.object({
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce
        .number()
        .int()
        .optional()
        .transform((value) => value ?? DEFAULT_CAMPAIGN_RESULT_PAGE_SIZE)
        .refine((value) => isCampaignResultPageSize(value), {
        message: "pageSize must be 25, 50, or 100",
    }),
    search: z.string().trim().max(200).optional(),
    resultStatus: z.enum(CAMPAIGN_RESULT_STATUSES).optional(),
});
export const campaignResultsRecipientsResponseSchema = z.object({
    items: z.array(campaignResultRecipientRowSchema),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    total: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
});
//# sourceMappingURL=campaign-contracts.js.map